<script setup lang="ts">
interface Props {
  throttle?: number
  duration?: number
  hideDelay?: number
  resetDelay?: number
  height?: number
  colorDark?: string
  colorLight?: string
  errorColor?: string
  estimatedProgress?: (duration: number, elapsed: number) => number
}

const colorMode = useColorMode()
const props = withDefaults(defineProps<Props>(), {
  throttle: 200,
  duration: 2000,
  hideDelay: 500,
  resetDelay: 400,
  colorDark: 'repeating-linear-gradient(to right,#7607ff 0%,var(--foreground) 50%,#2F0069 100%)',
  colorLight: 'repeating-linear-gradient(to right,#7607ff 0%,#FFBE7B 50%,#2F0069 100%)',
  errorColor: 'repeating-linear-gradient(to right,#f87171 0%,#ef4444 100%)',
})

const { progress, isLoading, error, start, finish, clear } = useLoadingIndicator({
  duration: props.duration,
  throttle: props.throttle,
  hideDelay: props.hideDelay,
  resetDelay: props.resetDelay,
  estimatedProgress: props.estimatedProgress,
})

const indicatorStyle = computed(() => ({
  position: 'fixed' as const,
  top: 0,
  right: 0,
  left: 0,
  pointerEvents: 'none' as const,
  width: 'auto',
  height: colorMode.preference === 'dark' ? '2px' : '3px',
  opacity: isLoading.value ? 1 : 0,
  background: error.value ? props.errorColor : (colorMode.preference === 'dark' ? props.colorDark : props.colorLight),
  backgroundSize: `${progress.value > 0 ? (100 / progress.value) * 100 : 0}% auto`,
  transform: `scaleX(${progress.value}%)`,
  transformOrigin: 'left',
  transition: 'transform 0.1s, height 0.4s, opacity 0.4s',
  zIndex: 999999,
}))

defineExpose({
  progress,
  isLoading,
  error,
  start,
  finish,
  clear,
})
</script>

<template>
  <div
    class="nuxt-loading-indicator"
    :style="indicatorStyle"
  />
</template>